import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, RefreshCw, Loader2, Download, Search, ChevronDown, ChevronRight, ToggleLeft, ToggleRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { logsService, LogEntry, getErrorMessage } from '@/services';
import dayjs from 'dayjs';
import { formatLogValue, isStructured } from '@/utils/log-value';
import ApiLogList, { ApiCallLoader, apiLogIdOf } from '@/components/logs/ApiLogList';

type TabType = 'stream' | 'panel' | 'error' | 'api';

export default function Logs() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('stream');
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isClearing, setIsClearing] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState<string>('');
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [autoRefresh, setAutoRefresh] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchLogs = useCallback(async () => {
    if (activeTab === 'api') return; // the API log tab loads its own data
    setIsLoading(true);
    try {
      const response = await logsService.searchLogs({
        search: search || undefined,
        log_type: activeTab,
        level: levelFilter || undefined,
        page,
        limit: 50,
      });
      setLogs(response.logs);
      setTotalPages(response.pagination.pages);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, page, search, levelFilter, t]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      autoRefreshRef.current = setInterval(() => {
        fetchLogs();
      }, 5000);
    } else {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
        autoRefreshRef.current = null;
      }
    }
    return () => {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
      }
    };
  }, [autoRefresh, fetchLogs]);

  const handleSearchChange = (value: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setSearch(value);
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
    }, 300);
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setPage(1);
    setExpandedIds(new Set());
  };

  const handleClear = async () => {
    setIsClearing(true);
    try {
      if (activeTab === 'stream') {
        await logsService.clearStreamLogs();
      } else if (activeTab === 'panel') {
        await logsService.clearPanelLogs();
      } else {
        await logsService.clearAll();
      }
      toast.success(t('logs.cleared'));
      setPage(1);
      fetchLogs();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    } finally {
      setIsClearing(false);
    }
  };

  const handleDownload = async () => {
    try {
      const blob = await logsService.downloadLogs({ log_type: activeTab });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `logs_${activeTab}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  const toggleExpanded = (id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'error': return 'text-error-600';
      case 'warn': return 'text-warning-600';
      case 'info': return 'text-success-600';
      case 'debug': return 'text-dark-500';
      default: return 'text-dark-400';
    }
  };

  const getLevelBadge = (level: string) => {
    switch (level) {
      case 'error': return 'bg-error-50 text-error-600';
      case 'warn': return 'bg-warning-50 text-warning-600';
      case 'info': return 'bg-success-50 text-success-600';
      case 'debug': return 'bg-dark-700 text-dark-500';
      default: return 'bg-dark-700 text-dark-400';
    }
  };

  const tabs: { key: TabType; label: string }[] = [
    { key: 'stream', label: t('logs.streamLogs') },
    { key: 'panel', label: t('logs.panelLogs') },
    { key: 'error', label: 'Error Logs' },
    { key: 'api', label: 'Log API' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">{t('logs.title')}</h1>
          <p className="text-dark-400 mt-1">{t('logs.message')}</p>
        </div>
        <div className="flex flex-wrap gap-2 md:gap-3">
          {/* Auto-refresh toggle */}
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`btn-secondary ${autoRefresh ? 'ring-2 ring-success-500' : ''}`}
            title={autoRefresh ? 'Auto-refresh ON (5s)' : 'Auto-refresh OFF'}
          >
            {autoRefresh ? <ToggleRight size={18} className="text-success-600" /> : <ToggleLeft size={18} />}
          </button>
          <button
            onClick={fetchLogs}
            disabled={isLoading}
            className="btn-secondary"
          >
            {isLoading ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
          </button>
          <button onClick={handleDownload} className="btn-secondary">
            <Download size={18} />
            {t('logs.downloadLogs')}
          </button>
          <button
            onClick={handleClear}
            disabled={isClearing}
            className="btn-danger"
          >
            {isClearing ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
            {t('logs.clearLogs')}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-dark-700">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-dark-400 hover:text-dark-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'api' ? (
        <ApiLogList admin={false} />
      ) : (
        <>
      {/* Search + Level Filter */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={t('common.search') + '...'}
            className="input pl-10 w-full"
          />
        </div>
        <select
          value={levelFilter}
          onChange={(e) => { setLevelFilter(e.target.value); setPage(1); }}
          className="input w-40"
        >
          <option value="">All Levels</option>
          <option value="debug">Debug</option>
          <option value="info">Info</option>
          <option value="warn">Warn</option>
          <option value="error">Error</option>
        </select>
      </div>

      {/* Logs */}
      {isLoading && logs.length === 0 ? (
        <div className="card">
          <div className="animate-pulse space-y-2">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="h-6 bg-dark-700 rounded" />
            ))}
          </div>
        </div>
      ) : logs.length === 0 ? (
        <div className="card">
          <p className="text-dark-400">{t('common.noData')}</p>
        </div>
      ) : (
        <div className="card font-mono text-sm overflow-x-auto">
          <div className="space-y-0.5">
            {logs.map((log) => {
              const isExpanded = expandedIds.has(log.id);
              const hasContext = !!log.context;

              return (
                <div key={log.id}>
                  <div
                    className={`flex items-start gap-3 py-1.5 px-2 rounded cursor-pointer hover:bg-dark-800 ${
                      isExpanded ? 'bg-dark-800' : ''
                    }`}
                    onClick={() => hasContext && toggleExpanded(log.id)}
                  >
                    {/* Expand icon */}
                    <span className="shrink-0 mt-0.5 w-4">
                      {hasContext ? (
                        isExpanded ? <ChevronDown size={14} className="text-dark-500" /> : <ChevronRight size={14} className="text-dark-500" />
                      ) : null}
                    </span>

                    {/* Timestamp */}
                    <span className="text-dark-500 shrink-0">
                      {dayjs(log.created_at).format('YYYY-MM-DD HH:mm:ss')}
                    </span>

                    {/* Level badge */}
                    <span className={`shrink-0 px-1.5 py-0.5 rounded text-xs font-medium ${getLevelBadge(log.level)}`}>
                      {log.level.toUpperCase()}
                    </span>

                    {/* Message */}
                    <span className={`break-all ${getLevelColor(log.level)}`}>
                      {log.message}
                    </span>
                  </div>

                  {/* Complete request and response of the API call this line is about */}
                  {isExpanded && apiLogIdOf(log.context) && (
                    <div className="ml-10 mb-2 px-3 py-2 bg-dark-800 rounded-lg border border-dark-700">
                      <ApiCallLoader id={apiLogIdOf(log.context)!} />
                    </div>
                  )}

                  {/* Expanded context */}
                  {isExpanded && log.context && (
                    <div className="ml-10 mb-2 px-3 py-2 bg-dark-800 rounded-lg border border-dark-700">
                      {(() => {
                        try {
                          const ctx = JSON.parse(log.context);
                          if (typeof ctx !== 'object' || ctx === null) {
                            return <pre className="text-xs text-dark-300 whitespace-pre-wrap">{log.context}</pre>;
                          }
                          return (
                            <div className="space-y-1.5 text-xs">
                              {Object.entries(ctx).map(([key, value]) => (
                                <div key={key} className="flex items-start gap-2">
                                  <span className="font-semibold text-dark-300 shrink-0 min-w-[100px]">{key}</span>
                                  <span className="text-dark-400">
                                    {Array.isArray(value) ? (
                                      <ul className="list-disc list-inside space-y-0.5">
                                        {value.map((item, i) => (
                                          <li key={i} className="font-mono whitespace-pre-wrap break-all">{formatLogValue(item)}</li>
                                        ))}
                                      </ul>
                                    ) : typeof value === 'boolean' ? (
                                      <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${value ? 'bg-success-50 text-success-600' : 'bg-error-50 text-error-600'}`}>
                                        {value ? 'true' : 'false'}
                                      </span>
                                    ) : typeof value === 'object' && value !== null ? (
                                      <pre className="font-mono text-[11px] bg-dark-900 p-2 rounded overflow-x-auto max-w-[600px]">
                                        {JSON.stringify(value, null, 2)}
                                      </pre>
                                    ) : isStructured(value) ? (
                                      <pre className="font-mono whitespace-pre-wrap break-all">{formatLogValue(value)}</pre>
                                    ) : (
                                      <span className="font-mono">{formatLogValue(value)}</span>
                                    )}
                                  </span>
                                </div>
                              ))}
                            </div>
                          );
                        } catch {
                          return <pre className="text-xs text-dark-300 whitespace-pre-wrap">{log.context}</pre>;
                        }
                      })()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-dark-700">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-secondary btn-sm"
              >
                {t('common.previous')}
              </button>
              <span className="text-dark-400 text-sm">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="btn-secondary btn-sm"
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </div>
      )}
        </>
      )}
    </div>
  );
}
